import { ReactNode, useEffect, useState } from "react";
import { Box, Button, Typography, useTheme, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { analytics } from "../../shared/utils";
import { HOOPER_NEON_GREEN, HOOPER_BLUE } from "../../shared/constants";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 600,
  bgcolor: "#16181A",
  boxShadow: 24,
  borderRadius: 2,
  textAlign: "center",
  pb: 4,
};

type InputProps = {
  parentViewName: string;
  backgroundId?: string;
  deepLinkPath?: string;
  children?: ReactNode;
};

const DownloadOverlay = ({
  parentViewName,
  backgroundId = "background-1.jpg",
  deepLinkPath,
  children,
}: InputProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [platform, setPlatform] = useState<"ios" | "android" | "unknown">(
    "unknown",
  );

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform("ios");
    } else if (/android/i.test(userAgent)) {
      setPlatform("android");
    }
  }, []);

  const handleOpenInApp = () => {
    if (!deepLinkPath) return;

    const openUrl = `hooper://${deepLinkPath}`;
    const fallbackUrl =
      platform === "ios"
        ? "https://apps.apple.com/us/app/hooper-basketball/id6458646707"
        : "https://play.google.com/store/apps/details?id=com.builtdifferent.hooper";

    analytics.track(`Web/${parentViewName}: clicked_open_in_app`, {
      deepLinkPath,
      platform,
    });

    let didHide = false;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") didHide = true;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    window.location.href = openUrl;

    setTimeout(() => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (!didHide) window.location.href = fallbackUrl;
    }, 1800);
  };

  return (
    <Box sx={style}>
      <img
        src={`/images/${backgroundId}`}
        alt="Hooper"
        style={{
          width: "100%",
          height: "200px",
          objectFit: "cover",
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        }}
      />
      <Box 
        sx={{
          px: !isMobile ? 4 : 2,
          pt: 2
        }}
      >
        <img
          src="/logos/logo-white.svg"
          alt="Hooper"
          style={{ width: 150, height: "auto", objectFit: "contain" }}
          onClick={() => {
            window.open(
              `https://hooper.gg?utm_source=public_view&utm_medium=${isMobile ? "mobile" : "desktop"}&utm_campaign=${parentViewName}`,
              "_blank",
            );
            analytics.track(
              `Web/${parentViewName}: clicked_hooper_website`,
              {
                isMobile: isMobile,
              },
            );
          }}
        />
        <Typography
          sx={{
            color: HOOPER_NEON_GREEN,
            fontSize: !isMobile ? 18 : 16,
            fontWeight: "bold",
          }}
          gutterBottom
        >
          Create your highlights, shot by shot.
        </Typography>
        {children && <Box sx={{ mb: 2 }}>{children}</Box>}
        {deepLinkPath && (
          <Button
            variant="contained"
            fullWidth
            onClick={handleOpenInApp}
            sx={{
              backgroundColor: HOOPER_BLUE,
              color: "white",
              fontSize: !isMobile ? 18 : 16,
              fontWeight: "bold",
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              mb: 2,
              "&:hover": {
                backgroundColor: "#6050FF",
              },
            }}
          >
            Open in the app
          </Button>
        )}
        <Typography
          sx={{
            color: "white",
            fontSize: !isMobile ? 16 : 14,
          }}
        >
          {deepLinkPath
            ? "Don't have the app? Download it here."
            : "For the Hooper experience, download the mobile app."}
        </Typography>
        <Grid
          container
          sx={{ justifyContent: "center", alignItems: "center", mt: 1 }}
          gap={2}
        >
          <Grid
            sx={{
              backgroundColor: "#5040FF",
              borderRadius: 2,
              px: 2,
              pt: 1.5,
              pb: 0.5,
              width: isMobile ? "100%" : "auto",
              height: 54,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              onClick={() => {
                window.open(
                  "https://apps.apple.com/us/app/hooper-basketball/id6458646707",
                  "_blank",
                );
                analytics.track(
                  `Web/${parentViewName}: clicked_apple_app_store`,
                );
              }}
            >
              <img 
                src="/images/appleappstore.svg" 
                alt="Apple App Store" 
                style={{ height: 50 }}
              />
            </Box>
          </Grid>
          <Grid
            sx={{
              backgroundColor: HOOPER_BLUE,
              borderRadius: 2,
              px: 2,
              pt: 1.5,
              pb: 0.5,
              width: isMobile ? "100%" : "auto",
              height: 54,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              onClick={() => {
                window.open(
                  "https://play.google.com/store/apps/details?id=com.builtdifferent.hooper",
                  "_blank",
                );
                analytics.track(`Web/${parentViewName}: clicked_google_play`);
              }}
            >
              <img 
                src="/images/googleapp.svg" 
                alt="Google App Store" 
                style={{ height: 35 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DownloadOverlay;
